/**
 * Account settings: the half of a person's preferences that follows them.
 *
 * TWO STORES, AND THE SPLIT IS NOT ARBITRARY. `appearance.ts` holds what must
 * NOT travel — palette, theme, density, text size, page size — because
 * `colour.md` is explicit that a shared family laptop should not repaint when
 * somebody switches account, and because a page size that suits a desktop is
 * wrong on a phone. This module holds the rest: choices about the mail rather
 * than about the screen it is on, which the origin stores per account and every
 * device of that person's reads back.
 *
 * IT IS DELIBERATELY NOT A CACHE. Every read is a request. The document is small
 * (one JSON object, no attachments, no lists of mail) and the failure mode of a
 * stale cache here — a device showing a preference somebody turned off on
 * another one — is the thing per-account storage exists to prevent.
 */
import { api, HearthError } from './api.ts'

/**
 * This browser's identity for the settings conflict rule.
 *
 * IT IS HALF THE TIE-BREAK, which is why the origin refuses a write without one
 * — see the `/settings` PUT handler. A default would make every device the same
 * device, and two of them racing would settle by arrival order, which is exactly
 * the divergence the tie-break exists to prevent.
 *
 * RANDOM AND PERSISTED, never derived from anything about the person: it names a
 * browser, and a value carrying an account or an address would be identity
 * leaking into a field that only has to be unique.
 */
const DEVICE_KEY = 'hearth.device'

export function deviceId(): string {
  try {
    const stored = localStorage.getItem(DEVICE_KEY)
    if (stored) return stored
    const fresh = crypto.randomUUID()
    localStorage.setItem(DEVICE_KEY, fresh)
    return fresh
  } catch {
    // A browser that refuses storage still has to be able to write a setting.
    // A per-session id is worse at the tie-break and better than a 400.
    return crypto.randomUUID()
  }
}

/** How the list groups mail. The registry's spelling, which is plural. */
export type Grouping = 'threads' | 'messages'

export const GROUPING_PATH = '/appearance/grouping'

/**
 * The API's spelling, which is SINGULAR, and the mapping is here rather than at
 * the call site because there is exactly one place it can go wrong.
 *
 * `decisions.md` fixed the setting's vocabulary before the search API served
 * either value; `?group=` was written later against its own. Neither is worth
 * changing now — a setting already written to accounts, and a query parameter
 * already in the API's error text — so the two spellings are reconciled once,
 * here.
 */
export const groupParam = (grouping: Grouping): 'thread' | 'message' =>
  grouping === 'threads' ? 'thread' : 'message'

const isGrouping = (value: unknown): value is Grouping => value === 'threads' || value === 'messages'

/**
 * Read one leaf out of the projected document.
 *
 * The document is nested — `{appearance: {grouping: 'messages'}}` — because that
 * is what `project()` builds from the leaf paths. Walking it here keeps every
 * caller working in leaf paths, which is the vocabulary the registry, the wire
 * and the conflict rule all use.
 */
function leaf(doc: Record<string, unknown>, path: string): unknown {
  let node: unknown = doc
  for (const segment of path.split('/').filter(Boolean)) {
    if (typeof node !== 'object' || node === null) return undefined
    node = (node as Record<string, unknown>)[segment]
  }
  return node
}

/**
 * The grouping this account prefers.
 *
 * FALLS BACK TO THE REGISTRY'S DEFAULT ON ANY FAILURE, and says nothing about
 * it. A settings read that 500s must not stop the mail appearing — the list has
 * a correct way to render either way, and a person who cannot see their mail
 * because a preference could not be fetched has a worse problem than a
 * preference that reverted.
 */
export async function readGrouping(): Promise<Grouping> {
  try {
    const { doc } = await api.settings()
    const value = leaf(doc, GROUPING_PATH)
    return isGrouping(value) ? value : 'messages'
  } catch {
    return 'messages'
  }
}

/** Write it back. Rejects, so a caller can tell the person it did not stick. */
export async function writeGrouping(grouping: Grouping): Promise<void> {
  await api.putSettings([{ path: GROUPING_PATH, value: grouping }], deviceId())
}

/**
 * How this account wants mail read to it: all four leaves, ONE request.
 *
 * FOUR LEAVES AND ONE `api.settings()`, and that is the reason this lives here
 * rather than in the shell. The module header says out loud that it is
 * deliberately not a cache and every read is a request; four readers would be
 * four round trips at 39-43 ms each on mount, for one document the origin
 * already projects whole.
 *
 * THE DEFAULTS COME FROM THE CALLER, not from literals here. `app.tsx` derives
 * them from the registry through `validateLeaf`, so `2000`, `fold`, `contacts`
 * and `right` are stated once in `packages/core/src/settings` and nowhere else.
 * Restating them here is the bug this whole file's shape exists to avoid.
 *
 * WHY THIS MATTERS MORE THAN THE OTHER READS: `mark_read_after_ms` is the only
 * preference in the set that WRITES TO THE MAIL SERVER. Without this function
 * the shell held the registry's default for ever, so `-1` — the account saying
 * "never mark anything read" — was unhonourable, and every household got
 * `+FLAGS (\Seen)` on the real IMAP server two seconds after a message rested
 * in the pane, visible in Thunderbird and on every phone, with no way to switch
 * it off. A default that cannot be overridden is not a default.
 *
 * FALLS BACK TO WHAT IT WAS GIVEN ON ANY FAILURE, for the reason `readGrouping`
 * gives: a settings read that fails must not stop the mail appearing.
 */
export async function readReadingPrefs<T extends ReadingPrefsShape>(fallback: T): Promise<T> {
  try {
    const { doc } = await api.settings()
    const pick = <V,>(path: string, current: V, ok: (value: unknown) => boolean): V => {
      const value = leaf(doc, path)
      return value !== undefined && ok(value) ? (value as V) : current
    }
    return {
      ...fallback,
      markReadAfterMs: pick('/reading/mark_read_after_ms', fallback.markReadAfterMs, (v) => typeof v === 'number'),
      quotedText: pick('/reading/quoted_text', fallback.quotedText, (v) => v === 'fold' || v === 'show'),
      remoteImages: pick(
        '/reading/remote_images',
        fallback.remoteImages,
        (v) => v === 'never' || v === 'contacts' || v === 'always',
      ),
      readingPane: pick(
        '/appearance/reading_pane',
        fallback.readingPane,
        (v) => v === 'right' || v === 'below' || v === 'none',
      ),
    }
  } catch {
    return fallback
  }
}

/**
 * What `readReadingPrefs` reads, structurally.
 *
 * DECLARED HERE AND OWNED BY THE SHELL, which is the awkward half of keeping
 * the defaults in one place: `ReadingPrefs` is derived from the registry in
 * `app.tsx`, and importing it here would make this module depend on the surface
 * that consumes it. The generic keeps the caller's exact type through the
 * round trip, so a leaf added there is a compile error here rather than a
 * silently dropped field.
 */
export interface ReadingPrefsShape {
  readonly markReadAfterMs: number
  readonly quotedText: 'fold' | 'show'
  readonly remoteImages: 'never' | 'contacts' | 'always'
  readonly readingPane: 'right' | 'below' | 'none'
}

/**
 * The Screener's master switch.
 *
 * THE WHOLE SUBSYSTEM HANGS OFF THIS ONE LEAF. `screenerEnabled()` at the
 * origin reads it out of the projected document and compiles the Sieve block
 * only when it is `true` — so the cards, the decisions and the seeded sender
 * table all exist and file precisely nothing until somebody has written it.
 * Nothing in this client could, which is what made a shipped subsystem inert.
 *
 * IT IS `false` UNTIL SOMEBODY SAYS OTHERWISE, and that default is not a
 * preference — it is `review.md`'s blocker. Over an empty `known_senders` table
 * the Screener's rule has nobody to exempt, so every sender there is would be
 * filed into a real IMAP folder and the Inbox would go empty on every device,
 * including the phones reading it over IMAP where there is nothing to click and
 * nothing that explains. Three locks stand between a household and that state —
 * this default, the origin's refusal to write `true` before the seed has found
 * anybody, and the compiler omitting the block while nobody is known. What is
 * written here goes through all three rather than around them.
 */
export const SCREENER_ENABLED_PATH = '/screener/enabled'

/**
 * Is the Screener filing mail for this account?
 *
 * IT REJECTS RATHER THAN FALLING BACK, which is the opposite of `readGrouping`
 * above and deliberate. A grouping that could not be fetched still has a
 * correct way to draw the list; this has none. Drawing "off" over an unknown
 * state tells somebody their mail is not being filed when it may be, and the
 * switch they then press writes the value they were already at.
 */
export async function readScreenerEnabled(): Promise<boolean> {
  const { doc } = await api.settings()
  // ANYTHING BUT `true` IS OFF — the same test the origin makes on the same
  // leaf (`doc.screener?.enabled === true`), rather than a second opinion here
  // about what a missing, tombstoned or hand-edited value means.
  return leaf(doc, SCREENER_ENABLED_PATH) === true
}

/** Write it. Rejects — including with the origin's readiness refusal below. */
export async function writeScreenerEnabled(enabled: boolean): Promise<void> {
  await api.putSettings([{ path: SCREENER_ENABLED_PATH, value: enabled }], deviceId())
}

/**
 * The origin's refusal to switch the Screener on, in its own words, or null.
 *
 * THIS CLIENT DOES NOT WRITE THAT SENTENCE. `PUT /settings` refuses
 * `/screener/enabled: true` while the sender table is empty and says why; a
 * second copy of the explanation here would be a second statement about the one
 * thing that must not have two, and it would go stale the first time the gate's
 * reason changed. Null for every other failure, so a caller can tell "the
 * server said no, and here is why" from "the request fell over".
 */
export function notReadyRefusal(error: unknown): string | null {
  if (!(error instanceof HearthError)) return null
  const refusals: unknown = error.body?.refusals
  if (!Array.isArray(refusals)) return null
  for (const entry of refusals as readonly unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue
    const { path, code, message } = entry as { path?: unknown; code?: unknown; message?: unknown }
    if (path === SCREENER_ENABLED_PATH && code === 'not_ready' && typeof message === 'string') return message
  }
  return null
}
